import _ from 'lodash';
import { initSuccess, loadOrganizations } from './actions';
import { duplicateCampaign } from '../../actions';
import { GraphQLClient } from 'graphql-request';
import { createHttp, getNextBase } from 'utils/http';
import c from 'common/constants/flux-events';

const ORGANIZATION_SELECTION = `
    id
    name
    currency
    default_timezone
    custom_fields {
        name
        value
        required
        key
    }
    type
    client_type
    revenueModel
    ftaEnabled
    fta_partner_id
    agencyMarginRate
    deprecated
`;

const OWN_ORGANIZATION_SELECTION = `
    id
    type
    fta_partner_id
    agencyMarginRate
    revenueModel
    users {
        first_name
        last_name
        id
        suspended
        global_role
        deprecated
    }
    ftaEnabled
    currency
    default_timezone
    custom_fields {
        name
        value
        required
        key
    }
`;

const GET_ORGANIZATIONS_QUERY = `
    query getOrganizations($filters: OrganizationFilters) {
        organizations(filters: $filters) {
            ${ORGANIZATION_SELECTION}
        }
        ownOrganization {
            ${OWN_ORGANIZATION_SELECTION}
        }
    }
`;

const GET_SELECTED_ORGANIZATIONS_QUERY = `
    query getSelectedOrganizations($filters: OrganizationFilters) {
        organizations(filters: $filters) {
            ${ORGANIZATION_SELECTION}
        }
    }
`;

const BASE_ORGANIZATION_FILTERS = {
    relationship: 'child',
    type: ['client', 'co_managed_client'],
};

const mw = () => ({ dispatch, getState }) => next => async action => {
    next(action);
    if (action.type === 'CAMPAIGN_DUPLICATE_MODAL__INIT') {
        try {
            const { campaign } = action.payload;
            const currentSelectionIds = _.chain(campaign.clients)
                .map('organization')
                .compact()
                .uniq()
                .value();
            const organizations = await getOrganizations({
                dispatch,
                getState,
                currentSelectionIds,
            });
            // Enrich campaign.clients with deprecated field from organizations
            const campaignClients = _.map(campaign.clients, client => {
                const org = _.find(organizations, { id: client.organization });
                return {
                    ...client,
                    deprecated: org ? org.deprecated : _.get(client, 'deprecated', false),
                };
            });

            const enrichedCampaign = {
                ...campaign,
                clients: campaignClients,
            };
            dispatch(initSuccess(enrichedCampaign, campaignClients, campaign.creatorId));
        } catch (err) {
            dispatch({
                type: 'CAMPAIGN_DUPLICATE_MODAL__INIT_ERROR',
                payload: {
                    error: err,
                },
            });
        }
    }
    if (action.type === 'CAMPAIGN_DUPLICATE_MODAL__CONFIRM_START') {
        const { campaignId, ...payload } = action.payload;
        const assigneesMapping = _.keyBy(
            _.get(getState(), 'campaignDuplicateModal.ownOrganization.users', []),
            'id'
        );
        const selectedCreatorId = _.get(payload, 'creatorId', '');
        const sanitizedCreatorId =
            selectedCreatorId && !assigneesMapping[selectedCreatorId] ? '' : selectedCreatorId;

        const sanitizedPayload = {
            ...payload,
            creatorId: sanitizedCreatorId,
            clients: _.map(payload.clients, client => _.omit(client, 'deprecated')),
        };
        await dispatch(duplicateCampaign(campaignId, sanitizedPayload));
    }
    if (action.type === c.OVERVIEW_CAMPAIGN__DUPLICATE__FAIL) {
        dispatch({
            type: 'CAMPAIGN_DUPLICATE_MODAL__CONFIRM_ERROR',
            payload: {
                error: action.payload.err,
            },
        });
    }
    if (action.type === c.OVERVIEW_CAMPAIGN__DUPLICATE__SUCCESS) {
        dispatch({
            type: 'CAMPAIGN_DUPLICATE_MODAL__CONFIRM_END',
        });
    }
};

async function getOrganizations({ dispatch, getState, currentSelectionIds = [] }) {
    try {
        const http = createHttp();

        const graphqlUrl = getNextBase() + 'graphql';

        const client = new GraphQLClient(graphqlUrl, {
            headers: {
                ...http._getHeaders(),
                Authorization: _.get(getState(), 'profile.authToken.auth_token'),
            },
        });

        const activeVariables = {
            filters: {
                ...BASE_ORGANIZATION_FILTERS,
                deprecated: false,
            },
        };

        const selectedOrganizationsPromise = _.isEmpty(currentSelectionIds)
            ? Promise.resolve({ organizations: [] })
            : client.request(GET_SELECTED_ORGANIZATIONS_QUERY, {
                  filters: {
                      ...BASE_ORGANIZATION_FILTERS,
                      organizationIds: currentSelectionIds,
                  },
              });

        const [data, selectedOrganizationsData] = await Promise.all([
            client.request(GET_ORGANIZATIONS_QUERY, activeVariables),
            selectedOrganizationsPromise,
        ]);

        let organizations = _.uniqBy(
            [
                ..._.get(data, 'organizations', []),
                ..._.get(selectedOrganizationsData, 'organizations', []),
            ],
            'id'
        );

        let formattedOrganizations = {};
        _.each(organizations, org => {
            formattedOrganizations[org.id] = {
                id: org.id,
                attr: org,
            };
        });

        let ownOrganization = data.ownOrganization;
        const { id, users } = ownOrganization;
        formattedOrganizations[id] = {
            ...formattedOrganizations[id],
            attr: {
                ...formattedOrganizations[id].attr,
                users,
            },
        };

        dispatch(
            loadOrganizations({
                organizations: formattedOrganizations,
                ownOrganization,
            })
        );

        return organizations;
    } catch (err) {
        console.error(`GET ALL organizations error`, err);

        throw err;
    }
}

export const makeMiddlewares = deps => {
    return [mw(deps)];
};

export const middlewares = makeMiddlewares({});
